import { Link } from 'react-router-dom';
import { useSyncLag } from '@/hooks/use-kitchen';
import { useT } from '@/i18n/use-t';

/** The shape `useT` returns, for the helper below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

function relative(iso: string | null, t: Translate): string {
  if (iso === null) return t('terminals.relative.never', 'never');
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  const ago = (key: string, en: string, n: number) => t(key, en).replace('{n}', String(n));
  if (seconds < 60) return ago('terminals.relative.secondsAgo', '{n}s ago', seconds);
  if (seconds < 3600)
    return ago('terminals.relative.minAgo', '{n} min ago', Math.floor(seconds / 60));
  if (seconds < 86_400)
    return ago('terminals.relative.hAgo', '{n} h ago', Math.floor(seconds / 3600));
  return ago('terminals.relative.dAgo', '{n} d ago', Math.floor(seconds / 86_400));
}

/**
 * FN-92 — the kitchen station's broker, printer and load, from the last
 * telemetry snapshot. Rendered only when the terminal reported a kitchen
 * block, so a POS or SCO detail page is unchanged.
 */
export function KitchenHealthCard({ terminalId }: { terminalId: string }) {
  const t = useT('admin_ui');
  const lag = useSyncLag(terminalId);
  const kitchen = lag.data?.kitchen;
  if (kitchen === undefined) return null;
  const colour =
    kitchen.level === 'green'
      ? 'bg-emerald-500'
      : kitchen.level === 'yellow'
        ? 'bg-amber-500'
        : 'bg-red-500';
  return (
    <section className="rounded-md border bg-card p-5" data-testid="kitchen-health-card">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-medium">{t('terminals.kitchen.station', 'Kitchen station')}</h2>
        <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <span className={`inline-block h-2 w-2 rounded-full ${colour}`} aria-hidden="true" />
          {kitchen.level}
          {kitchen.reason !== null && ` — ${kitchen.reason}`}
        </span>
      </div>
      <dl className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
        <dt className="text-muted-foreground">{t('terminals.kitchen.broker', 'Broker')}</dt>
        <dd>
          {kitchen.broker.configured
            ? kitchen.broker.connected
              ? kitchen.broker.consumerAlive
                ? t('terminals.kitchen.brokerConsuming', 'connected, consuming')
                : t('terminals.kitchen.brokerConsumerDead', 'connected, consumer not alive')
              : t('terminals.kitchen.brokerDisconnected', 'disconnected')
            : t('terminals.kitchen.brokerNotConfigured', 'not configured')}
          {kitchen.broker.queueName !== null && (
            <span className="text-muted-foreground"> · {kitchen.broker.queueName}</span>
          )}
        </dd>
        <dt className="text-muted-foreground">
          {t('terminals.kitchen.lastOrder', 'Last order received')}
        </dt>
        <dd title={kitchen.broker.lastMessageAt ?? undefined}>
          {relative(kitchen.broker.lastMessageAt, t)}
        </dd>
        <dt className="text-muted-foreground">
          {t('terminals.kitchen.outboxDepth', 'Status updates waiting')}
        </dt>
        <dd>{kitchen.broker.outboxDepth}</dd>
        <dt className="text-muted-foreground">
          {t('terminals.kitchen.activeOrders', 'Active orders')}
        </dt>
        <dd>{kitchen.activeOrders ?? '—'}</dd>
        <dt className="text-muted-foreground">
          {t('terminals.kitchen.labelPrinter', 'Label printer')}
        </dt>
        <dd>
          {kitchen.printer.configured
            ? `${
                kitchen.printer.ready
                  ? t('terminals.kitchen.printerReady', 'ready')
                  : t('terminals.kitchen.printerNotReady', 'not ready')
              } · ${kitchen.printer.model ?? ''} ${kitchen.printer.target ?? ''}`
            : t('terminals.kitchen.printerNotBound', 'not bound')}
          {kitchen.printer.lastError !== null && (
            <span className="text-destructive"> — {kitchen.printer.lastError}</span>
          )}
        </dd>
        <dt className="text-muted-foreground">
          {t('terminals.kitchen.lastSticker', 'Last sticker')}
        </dt>
        <dd>{relative(kitchen.printer.lastPrintAt, t)}</dd>
        {kitchen.broker.lastError !== null && (
          <>
            <dt className="text-muted-foreground">
              {t('terminals.kitchen.lastBrokerError', 'Last broker error')}
            </dt>
            <dd className="text-destructive">{kitchen.broker.lastError}</dd>
          </>
        )}
      </dl>
      <p className="mt-3 text-xs text-muted-foreground">
        {t('terminals.kitchen.reported', 'Reported {when}').replace(
          '{when}',
          relative(lag.data?.reportedAt ?? null, t),
        )}{' '}
        ·{' '}
        <Link className="underline" to={`/terminals/kitchen-orders`}>
          {t('terminals.kitchen.title', 'Kitchen orders')}
        </Link>
      </p>
    </section>
  );
}
